import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { type JSX, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import logActivityCardClicked from '@alltrails/amplitude/events/logActivityCardClicked';
import { getActivityUrl } from '@alltrails/cards/utils/cardLinks';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import useUser from '@alltrails/context/hooks/useUser';
import Image from '@alltrails/core/components/Image';
import ShimmerContainer from '@alltrails/core/components/ShimmerContainer';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import getActivityType from '@alltrails/core/utils/getActivityType';
import getStaticMapUrl from '@alltrails/core/utils/getStaticMapUrl';
import logError from '@alltrails/core/utils/logError';
import { getFormattedHoursMinutesWithUnitsInSpans, monthDayYear } from '@alltrails/data-formatting/dateTimeFormatters';
import { getFormattedDistanceWithUnitInSpan, getFormattedElevationWithUnitInSpan } from '@alltrails/data-formatting/distanceFormatters';
import Button from '@alltrails/denali/components/Button';
import StarRating from '@alltrails/denali/components/StarRating';
import UserLabel from '@alltrails/shared/components/UserLabel';
import { deleteTrack, getActivityFromTrack, removeTrack } from '@alltrails/tracks';
import type { Track } from '@alltrails/types/Track';
import type { TrailId } from '@alltrails/types/TrailId';
import { useAppSelector } from '../../utils/redux';
import { Stat, StatDivider } from './StatsComponents';
import SuggestAsRouteButton from './SuggestAsRouteButton';
import styles from './TrackCard.module.scss';
import useCardPhotos from './useCardPhotos';

const SwiperCarousel = dynamic(() => import('@alltrails/denali/components/SwiperCarousel'), {
  ssr: false
});

const activityMessages = defineMessages({
  DISTANCE: {
    defaultMessage: 'Distance'
  },
  ELV_GAIN: {
    defaultMessage: 'Elev. gain'
  },
  TIME: {
    defaultMessage: 'Time'
  }
});

export const LoadingTrackCard = () => (
  <ShimmerContainer>
    <div className={styles.contentLoading} data-testid="loading-track-card">
      <div className={styles.loadingHeader}>
        <div className={styles.imageLoading} />
        <div>
          <div className={styles.headerFirstLine} />
          <div className={styles.headerSecondLine} />
        </div>
      </div>
      <div>
        {new Array(5).fill('').map((_, i) => (
          <div className={styles.dataLine} key={i} />
        ))}
      </div>
    </div>
  </ShimmerContainer>
);

export type TrackCardProps = {
  track: Track;
  trailId: TrailId;
  userId?: number;
  onTrackDeleted?: (id: number) => void;
};

const TrackCard = ({ track, trailId, onTrackDeleted }: TrackCardProps): JSX.Element => {
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();
  const {
    formattedDefaultMessages: { DISTANCE, ELV_GAIN, TIME }
  } = useFormatMessage(activityMessages);
  const intl = useIntl();
  const displayMetric = useAppSelector(state => state.context.displayMetric);
  const [imageHovered, setImageHovered] = useState(false);
  const { hasPermission } = useAuthorization();
  const publicBaseUrl = usePublicBaseUrl();

  const isAdmin = hasPermission({ permission: 'trails:manage' });
  const isPendingOwner = user && user.id === track.user.id;
  const isAdminOrPendingOwner = isAdmin || isPendingOwner;

  const onTrackClick = () => {
    logActivityCardClicked({
      detailed_card_location: 'trail details activities view',
      map_id: track.id.toString(),
      owner_id: track.user.id.toString()
    });
  };

  const { activityType, hasPhotos, minutes, meters, elevationGain } = getActivityFromTrack(track);
  const activityLabel = getActivityType(intl, activityType);

  const href = getActivityUrl(track.slug, '', languageRegionCode);
  const { photos, loadPhotos } = useCardPhotos(track.id, track.photoCount);
  const staticMap = getStaticMapUrl({ baseUrl: publicBaseUrl, id: track.id, scale: 2, size: hasPhotos ? '160x160' : '320x320', type: 'maps' });

  const onMouseEnter = () => {
    loadPhotos();
    setImageHovered(true);
  };

  const onMouseLeave = () => {
    setImageHovered(false);
  };

  const handleTrackRemovedOrDeleted = (id: number) => {
    onTrackDeleted?.(id);
  };

  const onRemoveTrack = async e => {
    e.preventDefault();
    try {
      await removeTrack(track.id);
      handleTrackRemovedOrDeleted(track.id);
    } catch (e) {
      logError(new Error(`handleTrackRemovedError error: ${e}`));
    }
  };

  const onDeleteTrack = async e => {
    e.preventDefault();
    if (window.confirm('Are you sure you want to delete this activity from AllTrails?')) {
      try {
        await deleteTrack(track.id);
        handleTrackRemovedOrDeleted(track.id);
      } catch (e) {
        logError(new Error(`handleTrackDeletedError error: ${e}`));
      }
    }
  };

  const testIdPrefix = `ActivityCard_${track.name}`;

  return (
    <div className={styles.container} data-testid="track-card">
      <UserLabel
        languageRegionCode={languageRegionCode}
        secondaryText={
          <div className={styles.userSubheader}>
            {!!track.rating && <StarRating rating={track.rating} className={styles.starRating} />}
            <div>{monthDayYear(track.created_at, languageRegionCode)}</div>
            {!!activityLabel && (
              <>
                <div className={styles.dividerDot}>•</div>
                <div className={styles.activityLabel}>{activityLabel}</div>
              </>
            )}
          </div>
        }
        user={track.user}
      />
      <a className={styles.activityCardContainer} href={href} onClick={onTrackClick}>
        <div className={styles.photosContainer} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
          {!hasPhotos && (
            <div className={classNames(styles.carousel, imageHovered && styles.zoomPhoto)}>
              <Image src={staticMap} className={styles.image} loading="lazy" unoptimized alt="" />
            </div>
          )}
          {hasPhotos && photos.length > 0 && (
            <SwiperCarousel
              className={classNames(styles.carousel, imageHovered && styles.zoomPhoto)}
              uniqueId={track.name}
              items={photos}
              renderItem={src => {
                return <Image src={src as string} className={styles.image} loading="lazy" unoptimized alt="" />;
              }}
              getItemKey={(src, index) => `PhotoCarousel_${index}_${src}`}
              onFirstTouch={loadPhotos}
              arrowStyle="subtle"
              loop
              showPaginationDots
              hideSlideOverflow
            />
          )}
          {hasPhotos && <Image src={staticMap} className={styles.pinnedPhoto} loading="lazy" unoptimized alt="" />}
        </div>
        <div className={styles.data}>
          <div>
            <div className={classNames(styles.title, styles.truncating)} data-testid={`${testIdPrefix}_Title`}>
              {track.name}
            </div>
            <div className={styles.stats} data-testid={`${testIdPrefix}_Stats`}>
              <Stat label={DISTANCE} value={getFormattedDistanceWithUnitInSpan(intl, meters, displayMetric, undefined, true)} />
              <StatDivider />
              <Stat label={ELV_GAIN} value={getFormattedElevationWithUnitInSpan(intl, elevationGain, displayMetric, true)} />
              <StatDivider />
              <Stat label={TIME} value={getFormattedHoursMinutesWithUnitsInSpans(intl, minutes * 60, false)} />
            </div>
          </div>
          <div className={styles.buttons}>
            {!!user && <SuggestAsRouteButton track={track} trailId={trailId} isAdmin={isAdmin} isPendingOwner={isPendingOwner} />}
            {isAdmin && <Button onClick={onRemoveTrack} size="sm" text="Remove" variant="default" />}
            {isAdminOrPendingOwner && (
              <Button
                onClick={onDeleteTrack}
                size="sm"
                text={<FormattedMessage defaultMessage="Delete" />}
                variant="destructive"
              />
            )}
          </div>
        </div>
      </a>
    </div>
  );
};

export default TrackCard;
